import { FiChevronUp, FiMinus, FiPhone, FiPhoneOff, FiUser } from "react-icons/fi";

const THEME = { primary: "#FB565F" };

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function ButtonSpinner({ light = false }) {
  return (
    <span
      className={`inline-block h-4 w-4 animate-spin rounded-full border-2 ${
        light ? "border-white/40 border-t-white" : "border-current border-t-transparent"
      }`}
      aria-hidden
    />
  );
}

/**
 * Support call UI — floating widget so agents can keep chatting while on a call.
 */
export default function SupportCallModal({
  open,
  customerName = "Customer",
  phase = "ringing",
  durationSeconds = 0,
  minimized = false,
  endingCall = false,
  onMinimize,
  onExpand,
  onEndCall,
}) {
  if (!open) return null;

  const connecting = phase === "connecting";
  const ringing = phase === "ringing";
  const connected = phase === "connected";

  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[200] flex items-center gap-2 rounded-2xl bg-white shadow-2xl border border-gray-200 px-3 py-2.5 max-w-[min(100vw-2rem,360px)]">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: ringing ? "#16a34a" : THEME.primary }}
        >
          <FiPhone className="text-lg" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-900 truncate">
            {ringing ? "Ringing…" : "On call"}
          </p>
          <p className="text-sm font-mono font-bold text-emerald-600 tabular-nums">
            {ringing ? customerName : formatDuration(durationSeconds)}
          </p>
        </div>
        <button
          type="button"
          onClick={onExpand}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          title="Expand call"
          aria-label="Expand call"
        >
          <FiChevronUp className="text-lg" />
        </button>
        <button
          type="button"
          onClick={onEndCall}
          disabled={endingCall}
          className="inline-flex items-center justify-center p-2 rounded-full text-white disabled:opacity-70 min-w-[40px]"
          style={{ backgroundColor: THEME.primary }}
          title="End call"
          aria-label="End call"
        >
          {endingCall ? <ButtonSpinner light /> : <FiPhoneOff />}
        </button>
      </div>
    );
  }

  const headerTitle = connecting
    ? "Starting call"
    : ringing
      ? "Call started"
      : "Call connected";

  const headerSubtitle = connecting
    ? "Opening your call session…"
    : ringing
      ? "Ringing the customer — waiting for them to answer…"
      : "You are live with the customer";

  const statusLine = connecting ? (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-green-500" />
      Preparing…
    </span>
  ) : ringing ? (
    <span className="inline-flex items-center gap-1.5">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
      </span>
      Ringing on their device…
    </span>
  ) : (
    <>Connected · {formatDuration(durationSeconds)}</>
  );

  return (
    <div className="fixed bottom-4 right-4 z-[200] w-[min(100vw-2rem,380px)]">
      <div
        className="rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden"
        role="dialog"
        aria-modal="false"
        aria-labelledby="support-call-modal-title"
      >
        <div
          className="px-5 py-4 text-center text-white"
          style={{
            background:
              connecting || ringing
                ? "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                : `linear-gradient(135deg, ${THEME.primary} 0%, #e11d48 100%)`,
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="w-8" />
            <div
              className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/20 ${
                ringing ? "animate-pulse" : ""
              }`}
            >
              <FiPhone className="text-2xl text-white" />
            </div>
            {(connected || ringing) && (
              <button
                type="button"
                onClick={onMinimize}
                className="p-1.5 rounded-lg hover:bg-white/20 text-white"
                title="Minimize — keep chatting"
                aria-label="Minimize call"
              >
                <FiMinus className="text-lg" />
              </button>
            )}
          </div>
          <h2 id="support-call-modal-title" className="text-base font-bold">
            {ringing ? "Call started" : "Call connected"}
          </h2>
          <p className="text-xs text-white/90 mt-1">
            {ringing
              ? "Ringing the customer — waiting for them to answer…"
              : "You are live with the customer"}
          </p>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 mb-4">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: THEME.primary }}
            >
              <FiUser />
            </div>
            <div className="min-w-0 text-left flex-1">
              <p className="font-semibold text-gray-900 truncate">{customerName}</p>
              <p className="text-xs text-gray-500">{statusLine}</p>
            </div>
          </div>

          {connected && (
            <p className="text-xl font-mono font-bold text-emerald-600 mb-4 tabular-nums text-center">
              {formatDuration(durationSeconds)}
            </p>
          )}

          {ringing && (
            <p className="text-xs text-center text-gray-500 mb-3">
              Minimize to keep sending messages while you wait for them to answer.
            </p>
          )}

          <button
            type="button"
            onClick={onEndCall}
            disabled={endingCall}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-md hover:opacity-95 transition-opacity disabled:opacity-70"
            style={{ backgroundColor: THEME.primary }}
          >
            {endingCall ? (
              <>
                <ButtonSpinner light />
                Ending…
              </>
            ) : (
              <>
                <FiPhoneOff className="text-base" />
                End call
              </>
            )}
          </button>

          <p className="text-[10px] text-gray-400 mt-3 text-center">
            You can minimize this and continue the support chat during the call
          </p>
        </div>
      </div>
    </div>
  );
}
